import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { TextField, ThemeProvider } from '@mui/material';
import { createDebouncedUpdate, updateCellProperty, updateOutageEntry } from '../utils/InspectorUtils';

function computeDistanceMeters(lengthPx, direction) {
  const ratio = 0.3; // default ratio
  // if (direction === 'horizontal') ratio = 0.299;
  // else if (direction === 'vertical') ratio = 0.284;
  // else if (direction === 'diagonal') ratio = 0.219;
  return lengthPx * ratio;
}

function isStockCell(cell) {
  return String(cell.get?.('name') || '').toLowerCase() === 'stock';
}

function NumberInputFieldComponent({ field, cell, theme }) {
  const initial = cell.get(field.path) ?? field.defaultValue ?? '';
  const [value, setValue] = useState(initial);
  const [showSpeedFields, setShowSpeedFields] = useState(cell.get('useSpeed') === true);

  const cellId = field.cellId;
  const graph = cell.graph;

  const [lockFromStock, setLockFromStock] = useState(
    isStockCell(cell) && cell.get('useSameExitingProperties') === true
  );
  const isExitingParallelism = field.path === 'exitingParallelism';
  const isExitingCapacityPerHour = field.path === 'exitingCapacityPerHour';

  // Track useSpeed changes live
  useEffect(() => {
    const updateVisibility = () => setShowSpeedFields(cell.get('useSpeed') === true);
    updateVisibility();
    cell.on('change:attrs change:customAttributes', updateVisibility);
    return () => cell.off('change:attrs change:customAttributes', updateVisibility);
  }, [cell]);

  useEffect(() => {
    const onAnyChange = () => {
      setLockFromStock(isStockCell(cell) && cell.get('useSameExitingProperties') === true);
    };
    onAnyChange();
    cell.on('change:attrs change:customAttributes', onAnyChange);
    return () => cell.off('change:attrs change:customAttributes', onAnyChange);
  }, [cell]);

  const useSpeed = showSpeedFields;

  let isReadOnly = field.readOnly;
  if (field.path === 'speed' && !useSpeed) isReadOnly = true;
  if (field.path === 'duration' && useSpeed) isReadOnly = true;
  if (field.path === 'distanceMeters') isReadOnly = true;

  if (lockFromStock && (isExitingParallelism || isExitingCapacityPerHour)) {
    isReadOnly = true;
  }

  useEffect(() => {
    if (!(lockFromStock && (isExitingParallelism || isExitingCapacityPerHour))) return;

    const sync = () => {
      if (isExitingParallelism) {
        const v = Number(cell.get('parallelism')) || 0;
        setValue(v);
        updateCellProperty(cell, field, 'exitingParallelism', v, { silent: true, dirty: true });
      } else if (isExitingCapacityPerHour) {
        const v = Number(cell.get('capacityPerHour')) || 0;
        setValue(v);
        updateCellProperty(cell, field, 'exitingCapacityPerHour', v, { silent: true, dirty: true });
      }
    };

    sync();
    cell.on('change:attrs change:customAttributes', sync);
    return () => cell.off('change:attrs change:customAttributes', sync);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell, field.path, lockFromStock, isExitingParallelism, isExitingCapacityPerHour]);

  // Distance & duration auto-calculation
  useEffect(() => {
    if (field.path === 'distanceMeters' || field.path === 'duration') {
      const updateValues = () => {
        const lengthPx = Number(cell.get('length'));
        const direction = cell.get('direction') || 'horizontal';
        const distanceMeters = computeDistanceMeters(lengthPx, direction);
        const speed = Number(cell.get('speed')) || 0;
        const useSpeedNow = cell.get('useSpeed');

        if (field.path === 'distanceMeters') {
          setValue(distanceMeters);
          updateCellProperty(cell, field, 'distanceMeters', distanceMeters, {
            silent: true,
            dirty: true,
          });
        }

        if (field.path === 'duration') {
          if (useSpeedNow && speed > 0) {
            const duration = Math.max(1, Math.round(distanceMeters / speed));
            setValue(duration);
            updateCellProperty(cell, field, 'duration', duration, { silent: true, dirty: true });
          } else if (!useSpeedNow) {
            setValue(cell.get('duration') ?? '');
          }
        }
      };

      updateValues();
      cell.on('change:attrs change:customAttributes', updateValues);
      return () => cell.off('change:attrs change:customAttributes', updateValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell, field.path]);

  const debouncedSilentUpdate = useMemo(
    () =>
      createDebouncedUpdate((newValue) => {
        // Ignore manual edits for locked exiting* fields
        if (lockFromStock && (isExitingParallelism || isExitingCapacityPerHour)) return;

        const num = Number(newValue);
        if (isNaN(num)) return;

        if (field.group === 'outages') {
          updateOutageEntry(graph, cellId, 0, field.path, num);
        } else {
          updateCellProperty(cell, field, field.path, num, { silent: true, dirty: true });
        }

        const lengthPx = Number(cell.get('length'));
        const direction = cell.get('direction') || 'horizontal';
        const distanceMeters = computeDistanceMeters(lengthPx, direction);

        // Always update distanceMeters if useSpeed true
        if (useSpeed) {
          updateCellProperty(cell, field, 'distanceMeters', distanceMeters, {
            silent: false,
            dirty: true,
          });
          cell.trigger('change:attrs change:customAttributes', cell, {
            ...cell.attributes,
            customAttributes: {
              ...cell.attributes.customAttributes,
              distanceMeters,
            },
            dirty: true,
          });
        }

        // Compute duration when applicable
        if (useSpeed && field.path === 'speed' && num > 0) {
          const duration = Math.max(1, Math.round(distanceMeters / num));
          updateCellProperty(cell, field, 'duration', duration, { silent: false, dirty: true });
          cell.trigger('change:attrs change:customAttributes', cell, {
            ...cell.attributes,
            duration,
            dirty: true,
          });
        }
      }, 300),
    [graph, cellId, field, cell, useSpeed, lockFromStock, isExitingParallelism, isExitingCapacityPerHour]
  );

  useEffect(() => () => debouncedSilentUpdate.cancel(), [debouncedSilentUpdate]);

  const handleChange = (e) => {
    const v = e.target.value;
    setValue(v);
    debouncedSilentUpdate(v);
  };

  const handleBlur = () => {
    debouncedSilentUpdate.cancel();

    if (lockFromStock && (isExitingParallelism || isExitingCapacityPerHour)) return;

    if (value === '' || value === null || value === undefined) {
      if (field.group === 'outages') {
        updateOutageEntry(graph, cellId, 0, field.path, null);
      } else {
        updateCellProperty(cell, field, field.path, null);
        cell.trigger('change:attrs change:customAttributes', cell, {
          ...cell.attributes,
          [field.path]: null,
          dirty: true,
        });
      }
      return;
    }

    const num = Number(value);
    if (Number.isNaN(num)) return;

    if (field.group === 'outages') {
      updateOutageEntry(graph, cellId, 0, field.path, num);
    } else {
      updateCellProperty(cell, field, field.path, num);
      cell.trigger('change:attrs change:customAttributes', cell, {
        ...cell.attributes,
        [field.path]: num,
        dirty: true,
      });
    }

    const lengthPx = Number(cell.get('length'));
    const direction = cell.get('direction') || 'horizontal';
    const distanceMeters = computeDistanceMeters(lengthPx, direction);

    if (useSpeed && field.path === 'speed' && num > 0) {
      const duration = Math.max(1, Math.round(distanceMeters / num));
      updateCellProperty(cell, field, 'duration', duration);
      cell.trigger('change:attrs change:customAttributes', cell, {
        ...cell.attributes,
        duration,
        dirty: true,
      });
    }
  };

  // Conditionally hide speed/distanceMeters fields if useSpeed === false
  if (!useSpeed && (field.path === 'speed' || field.path === 'distanceMeters')) {
    return null;
  }

  return (
    <ThemeProvider theme={theme}>
      <TextField
        fullWidth
        size="small"
        type="number"
        variant="outlined"
        label={field.label}
        value={value}
        onChange={isReadOnly ? undefined : handleChange}
        onBlur={isReadOnly ? undefined : handleBlur}
        disabled={isReadOnly}
        required={field.required}
        error={field.required && !value}
        helperText={field.required && !value ? 'Ce champ est requis' : ''}
        onWheel={(e) => e.target.blur()}
        InputProps={{
          inputProps: {
            min: field.path === 'duration' ? 1 : field.min,
            max: field.max,
            step: field.step || 1,
            'data-cy': field.attributes?.['data-cy'],
          },
          sx: {
            '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
          },
        }}
      />
    </ThemeProvider>
  );
}

NumberInputFieldComponent.propTypes = {
  field: PropTypes.shape({
    path: PropTypes.string.isRequired,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    group: PropTypes.string,
    label: PropTypes.string,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    attributes: PropTypes.object,
    min: PropTypes.number,
    max: PropTypes.number,
    step: PropTypes.number,
  }).isRequired,
  cell: PropTypes.shape({
    get: PropTypes.func.isRequired,
    graph: PropTypes.object.isRequired,
    trigger: PropTypes.func.isRequired,
    on: PropTypes.func.isRequired,
    off: PropTypes.func.isRequired,
    attributes: PropTypes.object.isRequired,
  }).isRequired,
  theme: PropTypes.object.isRequired,
};

export function renderNumberInput(field, cell, theme) {
  const container = document.createElement('div');
  ReactDOM.render(<NumberInputFieldComponent field={field} cell={cell} theme={theme} />, container);
  return container;
}
